import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from '../utils/axios';
import toast from 'react-hot-toast';
import {
  MapPin,
  Phone,
  Calendar,
  Clock,
  CheckCircle,
  ClipboardList,
  RefreshCw,
  ExternalLink,
  ChevronRight,
  TrendingUp,
  AlertTriangle
} from 'lucide-react';

const renderStatusBadge = (status) => {
  switch (status) {
    case 'Site Visit Completed':
    case 'Completed':
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-xs font-bold border border-emerald-200">
          <CheckCircle className="h-3.5 w-3.5" />
          Visit Completed
        </span>
      );
    case 'Site Working Completed 100%':
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-green-50 text-green-700 rounded-full text-xs font-bold border border-green-200">
          <CheckCircle className="h-3.5 w-3.5" />
          100% Completed
        </span>
      );
    case 'Site Working In Progress':
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-bold border border-blue-200 animate-pulse">
          <RefreshCw className="h-3 w-3 animate-spin" style={{ animationDuration: '3s' }} />
          Working In Progress
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-amber-50 text-amber-700 rounded-full text-xs font-bold border border-amber-200 animate-pulse">
          <Clock className="h-3.5 w-3.5 animate-spin-slow" />
          Pending Visit
        </span>
      );
  }
};

const SalesSiteVisits = () => {
  const [siteVisits, setSiteVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  
  // Custom Confirmation Modal States
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmTargetStatus, setConfirmTargetStatus] = useState('');

  const fetchSiteVisits = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    if (silent) setIsRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/sales/site-visits', {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('📡 Fetched site visits for employee:', response.data.length);
      setSiteVisits(response.data);
    } catch (error) {
      console.error('Fetch site visits error:', error);
      if (!silent) toast.error('Failed to load your assigned site visits');
    } finally {
      if (!silent) setLoading(false);
      if (silent) setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchSiteVisits();
  }, [fetchSiteVisits]);

  const handleUpdateSiteVisitStatus = async (id, newStatus, force = false) => {
    const isTriggerStatus = newStatus === 'Site Working Completed 100%' || newStatus === 'Completed';
    // If setting to a trigger status, confirm first using custom modal
    if (isTriggerStatus && !force) {
      setConfirmTargetId(id);
      setConfirmTargetStatus(newStatus);
      setShowConfirmModal(true);
      return;
    }
    setActionLoadingId(id);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/sales/site-visits/${id}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (isTriggerStatus) {
        toast.success('Site work marked completed! Feedback request sent via WhatsApp. 🏡✨');
      } else {
        toast.success(`Status updated to "${newStatus}" successfully!`);
      }
      fetchSiteVisits();
    } catch (error) {
      console.error('Update status error:', error);
      toast.error(error.response?.data?.error || 'Failed to update status');
    } finally {
      setActionLoadingId(null);
      setShowConfirmModal(false);
      setConfirmTargetId(null);
      setConfirmTargetStatus('');
    }
  };

  // Metrics calculations
  const totalAssigned = siteVisits.length;
  const pendingVisits = siteVisits.filter(v => v.site_visit_status === 'Pending' || v.site_visit_status === 'Site Working In Progress').length;
  const completedVisits = siteVisits.filter(v => v.site_visit_status === 'Completed' || v.site_visit_status === 'Site Visit Completed' || v.site_visit_status === 'Site Working Completed 100%').length;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A5F38]"></div>
        <p className="mt-4 text-slate-500 font-bold text-sm tracking-wide">Loading your site visits...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-[#0A5F38]/5 to-[#C5A57D]/10 rounded-3xl p-8 border border-[#0A5F38]/10 shadow-sm relative overflow-hidden">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 relative z-10">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-extrabold text-[#0A5F38] tracking-tight">Site Visit Manager</h1>
              {isRefreshing && (
                <div className="flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-600 rounded-lg animate-pulse border border-emerald-100">
                  <RefreshCw className="h-3 w-3 animate-spin" />
                  <span className="text-[10px] font-bold uppercase tracking-wider">Refreshing</span>
                </div>
              )}
            </div>
            <p className="text-slate-600 text-base max-w-xl leading-relaxed">
              Track your assigned interior consultations, view site locations, and trigger customer review messages upon manual visit completion.
            </p>
          </div>
          <button
            onClick={() => fetchSiteVisits(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-white text-[#0A5F38] border border-[#0A5F38]/20 rounded-2xl hover:bg-slate-50 hover:shadow-md active:scale-95 transition-all font-bold text-sm shadow-sm"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh List
          </button>
        </div>
      </div>

      {/* Metric Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Assigned */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-all flex items-center justify-between group">
          <div className="space-y-1">
            <h3 className="text-2xl font-black text-[#0A5F38]">{totalAssigned}</h3>
            <p className="text-slate-600 text-sm font-bold tracking-tight">Total Assigned Visits</p>
          </div>
          <div className="w-12 h-12 bg-[#0A5F38]/10 rounded-2xl flex items-center justify-center text-[#0A5F38] group-hover:scale-110 transition-transform">
            <ClipboardList className="h-6 w-6" />
          </div>
        </div>

        {/* Pending Visits */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-all flex items-center justify-between group">
          <div className="space-y-1">
            <h3 className="text-2xl font-black text-amber-600">{pendingVisits}</h3>
            <p className="text-slate-600 text-sm font-bold tracking-tight">Pending Site Visits</p>
          </div>
          <div className="w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-600 group-hover:scale-110 transition-transform border border-amber-100">
            <Clock className="h-6 w-6" />
          </div>
        </div>

        {/* Completed Visits */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-all flex items-center justify-between group">
          <div className="space-y-1">
            <h3 className="text-2xl font-black text-emerald-600">{completedVisits}</h3>
            <p className="text-slate-600 text-sm font-bold tracking-tight">Completed Site Visits</p>
          </div>
          <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600 group-hover:scale-110 transition-transform border border-emerald-100">
            <CheckCircle className="h-6 w-6" />
          </div>
        </div>
      </div>

      {/* Main Visits Section */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <span>Consultation Schedule</span>
            <span className="px-2.5 py-0.5 bg-slate-100 text-slate-700 rounded-full text-xs font-bold">{siteVisits.length}</span>
          </h2>
        </div>

        {siteVisits.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {siteVisits.map((visit) => {
              const details = visit.consultation_details || {};
              const isCompleted = visit.site_visit_status === 'Completed' || visit.site_visit_status === 'Site Visit Completed' || visit.site_visit_status === 'Site Working Completed 100%';
              const isLoading = actionLoadingId === visit.id;

              return (
                <div
                  key={visit.id}
                  className={`p-6 transition-all flex flex-col lg:flex-row lg:items-center justify-between gap-6 hover:bg-slate-50/40 ${
                    isCompleted ? 'bg-emerald-50/5' : ''
                  }`}
                >
                  {/* Lead Info & Schedule details */}
                  <div className="flex-1 space-y-4">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                      <span className="text-slate-900 font-extrabold text-lg flex items-center gap-2">
                        {visit.phone}
                      </span>
                      {visit.name && visit.name !== 'Guest' && (
                        <span className="px-2.5 py-0.5 bg-slate-100 text-slate-700 rounded text-xs font-bold border border-slate-200">
                          {visit.name}
                        </span>
                      )}
                      
                      {renderStatusBadge(visit.site_visit_status)}
                    </div>

                    {/* Requirements Tags */}
                    {visit.requirements && visit.requirements.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {visit.requirements.map((req, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-bold border border-slate-200"
                          >
                            {req}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Detailed Metadata Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                      {/* Date & Time */}
                      <div className="flex items-center gap-2 text-slate-600 text-sm font-semibold">
                        <Calendar className="h-4.5 w-4.5 text-slate-400 shrink-0" />
                        <div>
                          <span className="text-slate-950 font-bold">
                            {details.date && details.date !== 'N/A' ? details.date : 'Unscheduled Date'}
                          </span>
                          {' at '}
                          <span className="text-slate-950 font-bold">
                            {details.time && details.time !== 'N/A' ? details.time : 'Unscheduled Time'}
                          </span>
                        </div>
                      </div>

                      {/* Site Address */}
                      <div className="flex items-start gap-2 text-slate-600 text-sm font-semibold">
                        <MapPin className="h-4.5 w-4.5 text-slate-400 shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <span className="text-slate-950 font-bold block truncate max-w-sm" title={details.location}>
                            {details.location || 'Location Not Specified'}
                          </span>
                          {details.mapsLink && (
                            <a
                              href={details.mapsLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#0A5F38] text-xs font-bold hover:underline inline-flex items-center gap-0.5 mt-0.5"
                            >
                              Get Directions
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions Column */}
                  <div className="flex flex-wrap items-center gap-3 lg:self-center">
                    {/* Status Dropdown */}
                    <div className="flex flex-col gap-1.5 min-w-[220px]">
                      <label className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider">Update Site Status</label>
                      <select
                        value={visit.site_visit_status || 'Pending'}
                        onChange={(e) => handleUpdateSiteVisitStatus(visit.id, e.target.value)}
                        disabled={isLoading}
                        className={`text-xs font-bold px-3 py-2.5 rounded-2xl border cursor-pointer outline-none transition-all shadow-sm ${
                          visit.site_visit_status === 'Site Visit Completed' || visit.site_visit_status === 'Completed'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 focus:ring-2 focus:ring-emerald-500 hover:bg-emerald-100/50'
                            : visit.site_visit_status === 'Site Working Completed 100%'
                            ? 'bg-green-50 text-green-700 border-green-200 focus:ring-2 focus:ring-green-500 hover:bg-green-100/50'
                            : visit.site_visit_status === 'Site Working In Progress'
                            ? 'bg-blue-50 text-blue-700 border-blue-200 focus:ring-2 focus:ring-blue-500 hover:bg-blue-100/50'
                            : 'bg-amber-50 text-amber-700 border-amber-200 focus:ring-2 focus:ring-amber-500 hover:bg-amber-100/50'
                        }`}
                      >
                        <option value="Pending" className="text-slate-800 font-semibold bg-white">⟳ Pending Visit</option>
                        <option value="Site Visit Completed" className="text-slate-800 font-semibold bg-white">✓ Site Visit Completed</option>
                        <option value="Site Working In Progress" className="text-slate-800 font-semibold bg-white">⚙ Site Working In Progress</option>
                        <option value="Site Working Completed 100%" className="text-slate-800 font-semibold bg-white">💯 Site Working Completed 100%</option>
                      </select>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center p-16 text-center">
            <div className="h-16 w-16 bg-[#0A5F38]/5 rounded-3xl flex items-center justify-center text-[#0A5F38] mb-4 border border-[#0A5F38]/10 animate-bounce-slow">
              <ClipboardList className="h-8 w-8" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-1">No site visits assigned to you</h3>
            <p className="text-slate-500 text-sm max-w-sm leading-relaxed">
              When the administrator assigns booked consultations to your profile, they will appear here with scheduling details and route directions.
            </p>
          </div>
        )}
      </div>

      {/* Custom Confirmation Modal */}
      {showConfirmModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-transparent animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl p-6 shadow-2xl border border-slate-100 max-w-md w-full animate-in zoom-in-95 duration-200 flex flex-col items-center text-center space-y-4">
            <div className="h-14 w-14 bg-amber-50 border border-amber-100 rounded-2xl flex items-center justify-center text-amber-600 shadow-sm animate-bounce-slow">
              <AlertTriangle className="h-7 w-7" />
            </div>
            
            <div className="space-y-2">
              <h3 className="text-xl font-extrabold text-slate-900">Confirm Site Work Completion</h3>
              <p className="text-slate-500 text-sm leading-relaxed font-medium">
                Are you sure the site work is 100% completed? This status will **instantly trigger** a WhatsApp feedback and rating request to the customer.
              </p>
            </div>
            
            <div className="flex items-center gap-3 w-full pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmTargetId(null);
                  setConfirmTargetStatus('');
                }}
                className="flex-1 py-3 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 font-bold rounded-2xl text-sm transition-all active:scale-95 shadow-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleUpdateSiteVisitStatus(confirmTargetId, confirmTargetStatus, true)}
                className="flex-1 py-3 bg-[#0A5F38] hover:bg-[#0A5F38]/90 text-white font-extrabold rounded-2xl text-sm transition-all active:scale-95 shadow-md shadow-[#0A5F38]/10"
              >
                Yes, Send Request
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        .animate-spin-slow {
          animation: spin 3s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-bounce-slow {
          animation: bounce 3s infinite;
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
      `}</style>
    </div>
  );
};

export default SalesSiteVisits;
